import * as React from 'react'
import * as R from 'ramda'
import Delete from 'material-ui/svg-icons/action/delete'
import ArrowDown from 'material-ui/svg-icons/hardware/keyboard-arrow-down'

import {
  TextButton,
  FeedbackFormWrapper,
  FeedbackContent,
  FeedbackFormButton
} from './FeedbackStyles'
import { withStoreFeedback, WithFeedbackProps } from './feedbackQueries'
import { debounce } from '../../util/debounce'
import FeedbackPage from './FeedbackPage'
import { recordEvent, recordException } from '../../analytics/index'
import themeObj from '../../theme/defaultTheme'

const LocalStorageFeedback = 'deltabeam-select-feedback'

type SubTopic = {
  title: string
  rating?: number
}

type FrontPage = { type: 'front' }
export type ContentPage = {
  id: string // ID for storing these to local storage
  type: 'content'
  title: string
  rating?: number
  text: string
  hideRating?: boolean
  detailedQuestions: SubTopic[]
}

type Page = FrontPage | ContentPage

type InputProps = { open?: boolean; closeFeedback: () => void }
type Props = InputProps & WithFeedbackProps

type State = {
  page: number
  pages: Page[]
  busy: boolean
  error: string | null
}

const data = require('./questionnaire.json')

const questionnaire: Page[] = data

function isContent(p: Page): p is ContentPage {
  return p.type === 'content'
}

function feedbackResponses(pages: Page[]) {
  return pages
    .filter(isContent)
    .map(({ id, text, rating, detailedQuestions }) => ({
      id,
      text,
      rating,
      detailedQuestions
    }))
}

class FeedbackForm extends React.Component<Props, State> {
  updateStorage: () => void

  constructor(props: Props) {
    super(props)

    const stored = localStorage.getItem(LocalStorageFeedback)
    const pages: Page[] = this.populatePages(stored, questionnaire)

    this.state = { page: 0, pages, error: null, busy: false }
    this.updateStorage = debounce(this.updateLocalStorage, 500)
  }

  updateLocalStorage() {
    localStorage.setItem(
      LocalStorageFeedback,
      JSON.stringify(feedbackResponses(this.state.pages))
    )
  }

  clearFeedback = () => {
    localStorage.removeItem(LocalStorageFeedback)
    this.setState({ pages: questionnaire, page: 0, error: null })
  }

  nextPage = () => {
    const nextPage = Math.min(this.state.pages.length - 1, this.state.page + 1)
    recordEvent('feedback', 'feedback-page-change', { value: nextPage })
    this.setState({
      error: null,
      page: nextPage
    })
  }

  prevPage = () =>
    this.setState({
      error: null,
      page: Math.max(0, this.state.page - 1)
    })

  submit = () => {
    this.setState({ busy: true })
    this.props
      .storeFeedback(JSON.stringify(feedbackResponses(this.state.pages)))
      .then(response => {
        let error: string | null = null
        if (response.data.storeFeedback) {
          recordEvent('feedback', 'feedback-submitted')
          this.clearFeedback()
          this.props.closeFeedback()
        } else {
          console.error("Feedback couldn't be saved")
          recordException('feedback-submit-failed')
          error =
            "Feedback form could not be saved. Please try again later, we've stored your feedback locally " +
            'so you will not have to type it again.'
        }
        this.setState({ page: error ? this.state.page : 0, busy: false, error })
      })
      .catch(error => {
        recordException('feedback-submit-failed')
        console.error('Could not save feedback', error)
        this.setState({
          busy: false,
          error:
            "There was an issue saving your feedback. Please try again later, we've stored your feedback " +
            'locally so you will not have to type it again.'
        })
      })
  }

  setRating = (idx, rating) => {
    const page = this.state.pages[idx]

    if (page && isContent(page)) {
      this.setState({
        pages: R.adjust<Page>(R.assoc('rating', rating), idx, this.state.pages),
        error: null
      })
      this.updateStorage()
    }
  }

  setText = (idx, text) => {
    const page = this.state.pages[idx]

    if (page && isContent(page)) {
      this.setState({
        pages: R.adjust<Page>(R.assoc('text', text), idx, this.state.pages),
        error: null
      })
      this.updateStorage()
    }
  }

  setDetailRating = (idx, title, rating) => {
    const page = this.state.pages[idx]

    // Find the subtopic by title and update its rating
    const updateDetail = (r: number) => (p: Page): Page => {
      if (!isContent(p) || !p.detailedQuestions) {
        return p
      }
      const questionIdx = R.findIndex(
        R.propEq('title', title),
        p.detailedQuestions
      )
      if (questionIdx !== -1) {
        return {
          ...p,
          detailedQuestions: R.adjust(
            R.assoc('rating', r),
            questionIdx,
            p.detailedQuestions
          )
        }
      }
      return p
    }

    if (page && isContent(page)) {
      this.setState({
        pages: R.adjust<Page>(updateDetail(rating), idx, this.state.pages),
        error: null
      })
      this.updateStorage()
    }
  }

  renderFirstPage() {
    return (
      <FeedbackFormWrapper>
        <div className="feedback-modal first-page">
          <FeedbackContent>
            <div className="split">
              <div className="left">
                <h2>Give us feedback and help to develop the solution.</h2>
              </div>
              <div className="right">
                Send us your feedback with this form. Once opened, you may hide
                it and continue working. Return to the form at any time to add
                further comments.
              </div>
            </div>
          </FeedbackContent>
          <div className="feedback-buttons">
            <FeedbackFormButton onClick={this.props.closeFeedback} clear={true}>
              Later
            </FeedbackFormButton>
            <FeedbackFormButton onClick={this.nextPage} primary={true}>
              Next
            </FeedbackFormButton>
          </div>
        </div>
      </FeedbackFormWrapper>
    )
  }

  render() {
    if (!this.props.open) {
      return null
    }

    const { pages, page } = this.state

    const selectedPage = pages[page]
    if (!isContent(selectedPage)) {
      return this.renderFirstPage()
    }

    const numContentPages = R.filter(isContent, pages).length

    // Number of sub questions the user has not provided an answer for
    const unAsweredSubTopics = selectedPage.detailedQuestions.filter(d =>
      R.isNil(d.rating)
    ).length

    const disallowNext =
      this.state.busy ||
      selectedPage.text.trim() === '' ||
      (selectedPage.rating === undefined && !selectedPage.hideRating) ||
      unAsweredSubTopics > 0

    return (
      <FeedbackFormWrapper>
        <div className="feedback-modal">
          <div className="title-line">
            <h3>
              Part {page}/{numContentPages}
            </h3>
            <TextButton onClick={this.props.closeFeedback}>
              Hide <ArrowDown color={themeObj.colors.invert} />
            </TextButton>
          </div>
          <FeedbackContent>
            <FeedbackPage
              setRating={this.setRating}
              setText={this.setText}
              pageIdx={page}
              page={selectedPage}
              setDetailRating={this.setDetailRating}
            />
            <TextButton onClick={this.clearFeedback}>
              Clear all form fields <Delete color={themeObj.colors.light} />
            </TextButton>
          </FeedbackContent>
          <div className="feedback-buttons">
            <FeedbackFormButton onClick={this.prevPage}>
              Prev
            </FeedbackFormButton>
            {this.state.page === this.state.pages.length - 1 ? (
              <FeedbackFormButton
                onClick={this.submit}
                primary={true}
                disabled={disallowNext}
              >
                Send feedback
              </FeedbackFormButton>
            ) : (
              <FeedbackFormButton
                onClick={this.nextPage}
                primary={true}
                disabled={disallowNext}
              >
                Next
              </FeedbackFormButton>
            )}
          </div>
          {this.state.error && <div>{this.state.error}</div>}
        </div>
      </FeedbackFormWrapper>
    )
  }

  /* We store id, rating and text in local storage (rather than the whole
    state.pages array) because client may have stored items that are
    different from the latest questions the client wants to show. */
  private populatePages(stored: string | null, pages: Page[]): Page[] {
    if (!stored) {
      return pages
    }

    let storedResponses
    try {
      storedResponses = JSON.parse(stored)
    } catch (e) {
      return pages
    }

    return pages.map(page => {
      if (!isContent(page)) {
        return page
      }

      const resp = R.find(R.propEq('id', page.id), storedResponses)

      if (!resp) {
        return page
      }

      return {
        ...page,
        text: resp.text || '',
        rating: resp.rating,
        detailedQuestions: resp.detailedQuestions
      }
    })
  }
}

export default withStoreFeedback<InputProps>()(FeedbackForm)



// WEBPACK FOOTER //
// ./src/Components/FeedbackForm/index.tsx